/** Helpers for merging Dashboard/stats series and shaping regional API data */

import { subDays, subMonths, subYears } from "date-fns";

export function formatDateParam(d) {
  if (!d) return "";
  if (typeof d === "string") return d.slice(0, 10);
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return "";
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function defaultTrendRange() {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 30);
  return { start: formatDateParam(start), end: formatDateParam(end) };
}

/** Preset windows ending today for participation / trend charts (1m ≈ 30d). */
export function trendRangeFromPreset(preset) {
  const end = new Date();
  let start;
  switch (preset) {
    case "1m":
      start = subDays(end, 30);
      break;
    case "3m":
      start = subMonths(end, 3);
      break;
    case "6m":
      start = subMonths(end, 6);
      break;
    case "1y":
      start = subYears(end, 1);
      break;
    default:
      start = subDays(end, 30);
  }
  return { start: formatDateParam(start), end: formatDateParam(end) };
}

/**
 * Align teacher + establishment daily stats on a shared sorted date axis.
 */
export function mergeDailySeries(teacherStats = [], establishmentStats = []) {
  const keys = new Set();
  for (const row of teacherStats) {
    if (row?.attendanceDate) keys.add(row.attendanceDate.split("T")[0]);
  }
  for (const row of establishmentStats) {
    if (row?.attendanceDate) keys.add(row.attendanceDate.split("T")[0]);
  }
  const labels = Array.from(keys).sort();
  const mapTeachers = new Map(
    teacherStats.map((row) => [row.attendanceDate.split("T")[0], row])
  );
  const mapEst = new Map(
    establishmentStats.map((row) => [row.attendanceDate.split("T")[0], row])
  );

  const teachersDaily = labels.map((d) => mapTeachers.get(d)?.dayWiseTeachers ?? 0);
  const establishmentsDaily = labels.map((d) => mapEst.get(d)?.dayWiseEstablishments ?? 0);
  const teacherPct = labels.map((d) => {
    const row = mapTeachers.get(d);
    const o = row?.overallTeachers ?? 0;
    return o > 0 ? (row.dayWiseTeachers / o) * 100 : 0;
  });
  const establishmentPct = labels.map((d) => {
    const row = mapEst.get(d);
    const o = row?.overallEstablishments ?? 0;
    return o > 0 ? (row.dayWiseEstablishments / o) * 100 : 0;
  });

  return { labels, teachersDaily, establishmentsDaily, teacherPct, establishmentPct };
}

export function filterRegional(rows, categoryType) {
  return (rows || []).filter((r) => r.categoryType === categoryType);
}

export function sumEstablishmentMetrics(rows) {
  const est = filterRegional(rows, "Establishment");
  if (!est.length) {
    const first = rows?.[0];
    return {
      reportedSum: 0,
      withinRadiusSum: 0,
      rosterTotal: first?.totalTeachers ?? 0,
    };
  }
  return {
    reportedSum: est.reduce((s, r) => s + (Number(r.teachersReported) || 0), 0),
    withinRadiusSum: est.reduce((s, r) => s + (Number(r.teachersWithinRadius) || 0), 0),
    rosterTotal: Number(est[0]?.totalTeachers) || 0,
  };
}

/** Bar chart config for reports/statistical rows (Province | Department | Establishment). */

export function buildDoughnutTeachersReported(rows, translate) {
  const list = rows || [];
  const palette = ["info", "success", "warning", "error", "dark", "primary", "secondary"];
  if (!list.length) {
    return {
      labels: ["—"],
      datasets: {
        label: translate("statistics.chart.reported_share", "Reported teachers"),
        data: [0],
        backgroundColors: ["dark"],
      },
      cutout: 62,
    };
  }
  return {
    labels: list.map((r) => r.name || "—"),
    datasets: {
      label: translate("statistics.chart.reported_share", "Reported teachers by row"),
      data: list.map((r) => Number(r.teachersReported) || 0),
      backgroundColors: list.map((_, i) => palette[i % palette.length]),
    },
    cutout: 62,
  };
}

/** Radar — reported vs within radius (reports screen). */
export function buildRadarComparisonChart(rows, translate) {
  const list = rows || [];
  if (!list.length) {
    return {
      labels: ["—"],
      datasets: [
        {
          label: translate("dashboard.chart.reported", "Reported"),
          color: "info",
          data: [0],
        },
        {
          label: translate("dashboard.chart.within_radius", "Within radius"),
          color: "success",
          data: [0],
        },
      ],
    };
  }
  return {
    labels: list.map((r) => r.name || "—"),
    datasets: [
      {
        label: translate("dashboard.chart.reported", "Reported"),
        color: "info",
        data: list.map((r) => Number(r.teachersReported) || 0),
      },
      {
        label: translate("dashboard.chart.within_radius", "Within radius"),
        color: "success",
        data: list.map((r) => Number(r.teachersWithinRadius) || 0),
      },
    ],
  };
}

export function buildRegionalChart(rows, categoryType, translate) {
  const filtered = filterRegional(rows || [], categoryType);
  if (!filtered.length) {
    return {
      labels: ["—"],
      datasets: [
        {
          label: translate("dashboard.chart.reported", "Reported"),
          color: "info",
          data: [0],
        },
        {
          label: translate("dashboard.chart.within_radius", "Within radius"),
          color: "success",
          data: [0],
        },
      ],
    };
  }
  return {
    labels: filtered.map((r) => r.name || "—"),
    datasets: [
      {
        label: translate("dashboard.chart.reported", "Reported"),
        color: "info",
        data: filtered.map((r) => Number(r.teachersReported) || 0),
      },
      {
        label: translate("dashboard.chart.within_radius", "Within radius"),
        color: "success",
        data: filtered.map((r) => Number(r.teachersWithinRadius) || 0),
      },
    ],
  };
}

/** Normalize teacher quota fields from GET Dashboard/stats. */
export function parseDashboardTeacherQuota(data) {
  const licensedTeachers = Number(
    data?.LicensedTeachers ?? data?.licensedTeachers ?? data?.LicensedCount ?? data?.licensedCount ?? 0
  );
  const maxLicensedTeachers = Number(data?.MaxLicensedTeachers ?? data?.maxLicensedTeachers ?? 0);
  const totalTeachers = Number(data?.TotalTeachers ?? data?.totalTeachers ?? 0);
  const remainingLicenses = Number(
    data?.AllowedTeachers ??
      data?.allowedTeachers ??
      data?.RemainingLicenses ??
      data?.remainingLicenses ??
      Math.max(0, maxLicensedTeachers - licensedTeachers)
  );
  return { licensedTeachers, maxLicensedTeachers, totalTeachers, remainingLicenses };
}

/** Normalize summary fields from GET TeacherLicense/summary. */
export function parseTeacherLicenseSummary(data) {
  const licensedCount = Number(data?.LicensedCount ?? data?.licensedCount ?? 0);
  const unlicensedCount = Number(data?.UnlicensedCount ?? data?.unlicensedCount ?? 0);
  const totalTeachers = Number(data?.TotalTeachers ?? data?.totalTeachers ?? 0);
  const maxLicensedTeachers = Number(data?.MaxLicensedTeachers ?? data?.maxLicensedTeachers ?? 0);
  const remainingLicenses = Number(
    data?.RemainingLicenses ??
      data?.remainingLicenses ??
      data?.AllowedTeachers ??
      data?.allowedTeachers ??
      Math.max(0, maxLicensedTeachers - licensedCount)
  );
  return { licensedCount, unlicensedCount, totalTeachers, maxLicensedTeachers, remainingLicenses };
}
